import React, { useState, useEffect } from 'react';
import { Brain, Shield, Database, Zap, GitBranch, Code, Play, Pause, RotateCcw, CheckCircle, ArrowRight, Lock, Server, AlertTriangle, Users, FileText, TrendingUp } from 'lucide-react';

const HomeAgentSimulation = () => {
  const [activeTab, setActiveTab] = useState('recruitment');
  const [isAnimating, setIsAnimating] = useState(true);
  const [workflowStep, setWorkflowStep] = useState(0);

  useEffect(() => {
    if (!isAnimating) return;
    
    const interval = setInterval(() => {
      const stepCounts = {
        'recruitment': 7,
        'call-support': 6,
        'pmo': 8,
        'credit-risk': 7
      };
      setWorkflowStep(prev => (prev + 1) % (stepCounts[activeTab] || 7));
    }, 2000);
    
    return () => clearInterval(interval);
  }, [isAnimating, activeTab]);

  const aiApplications = {
    'recruitment': {
      name: 'AI Recruitment Assistant',
      description: 'Intelligent candidate screening and interview scheduling',
      icon: <Users />,
      color: 'from-blue-500 to-cyan-500',
      workflow: [
        { step: 'Resume Parsing', icon: <FileText className="w-5 h-5" />, desc: 'Extract skills, experience, education', color: 'from-blue-500 to-cyan-500', x: '10%', y: '30%' },
        { step: 'Candidate Matching', icon: <Brain className="w-5 h-5" />, desc: 'AI-powered job fit analysis', color: 'from-purple-500 to-pink-500', x: '30%', y: '30%' },
        { step: 'Skill Assessment', icon: <CheckCircle className="w-5 h-5" />, desc: 'Automated technical evaluation', color: 'from-green-500 to-emerald-500', x: '50%', y: '30%' },
        { step: 'Interview Scheduling', icon: <Database className="w-5 h-5" />, desc: 'Calendar optimization', color: 'from-orange-500 to-red-500', x: '70%', y: '30%' },
        { step: 'Sentiment Analysis', icon: <TrendingUp className="w-5 h-5" />, desc: 'Communication analysis', color: 'from-indigo-500 to-purple-500', x: '90%', y: '30%' },
        { step: 'Decision Engine', icon: <GitBranch className="w-5 h-5" />, desc: 'Ranking & recommendation', color: 'from-pink-500 to-rose-500', x: '50%', y: '70%' },
        { step: 'Notification System', icon: <AlertTriangle className="w-5 h-5" />, desc: 'Multi-channel updates', color: 'from-yellow-500 to-orange-500', x: '30%', y: '70%' },
      ],
      stats: { processed: '1,247', matched: '342', interviews: '89', hired: '23' }
    },
    'call-support': {
      name: 'AI Call Support Center',
      description: 'Intelligent voice assistant with real-time decision support',
      icon: <Server />,
      color: 'from-purple-500 to-pink-500',
      workflow: [
        { step: 'Voice Recognition', icon: <Database className="w-5 h-5" />, desc: 'Real-time speech-to-text', color: 'from-blue-500 to-cyan-500', x: '10%', y: '30%' },
        { step: 'Intent Detection', icon: <Brain className="w-5 h-5" />, desc: 'Understand customer need', color: 'from-purple-500 to-pink-500', x: '30%', y: '30%' },
        { step: 'Context Retrieval', icon: <Database className="w-5 h-5" />, desc: 'Customer history & knowledge', color: 'from-green-500 to-emerald-500', x: '50%', y: '30%' },
        { step: 'Response Generation', icon: <Code className="w-5 h-5" />, desc: 'AI-powered suggestions', color: 'from-orange-500 to-red-500', x: '70%', y: '30%' },
        { step: 'Sentiment Monitor', icon: <TrendingUp className="w-5 h-5" />, desc: 'Emotional intelligence', color: 'from-indigo-500 to-purple-500', x: '90%', y: '30%' },
        { step: 'Escalation Router', icon: <GitBranch className="w-5 h-5" />, desc: 'Smart ticket routing', color: 'from-pink-500 to-rose-500', x: '50%', y: '70%' },
      ],
      stats: { calls: '3,892', resolved: '3,124', avgTime: '4.2m', satisfaction: '94%' }
    },
    'pmo': {
      name: 'PMO Intelligence Platform',
      description: 'AI-powered project management and risk prediction',
      icon: <GitBranch />,
      color: 'from-green-500 to-emerald-500',
      workflow: [
        { step: 'Data Aggregation', icon: <Database className="w-5 h-5" />, desc: 'Multi-source project data', color: 'from-blue-500 to-cyan-500', x: '8%', y: '20%' },
        { step: 'Task Analysis', icon: <Code className="w-5 h-5" />, desc: 'Dependencies & bottlenecks', color: 'from-purple-500 to-pink-500', x: '25%', y: '20%' },
        { step: 'Risk Prediction', icon: <AlertTriangle className="w-5 h-5" />, desc: 'ML-based forecasting', color: 'from-red-500 to-orange-500', x: '42%', y: '20%' },
        { step: 'Resource Optimization', icon: <Users className="w-5 h-5" />, desc: 'Team allocation AI', color: 'from-green-500 to-emerald-500', x: '59%', y: '20%' },
        { step: 'Timeline Adjustment', icon: <TrendingUp className="w-5 h-5" />, desc: 'Dynamic rescheduling', color: 'from-indigo-500 to-purple-500', x: '76%', y: '20%' },
        { step: 'Stakeholder Updates', icon: <FileText className="w-5 h-5" />, desc: 'Auto-generated reports', color: 'from-pink-500 to-rose-500', x: '92%', y: '20%' },
        { step: 'Action Orchestrator', icon: <GitBranch className="w-5 h-5" />, desc: 'Workflow automation', color: 'from-cyan-500 to-blue-500', x: '50%', y: '50%' },
        { step: 'Integration Hub', icon: <Zap className="w-5 h-5" />, desc: 'Jira, Asana, MS Project', color: 'from-yellow-500 to-orange-500', x: '50%', y: '80%' },
      ],
      stats: { projects: '47', onTrack: '38', atRisk: '7', critical: '2' }
    },
    'credit-risk': {
      name: 'Credit Risk Analyzer',
      description: 'Real-time creditworthiness assessment and decision support',
      icon: <Shield />,
      color: 'from-orange-500 to-red-500',
      workflow: [
        { step: 'Data Collection', icon: <Database className="w-5 h-5" />, desc: 'Credit bureau & financial data', color: 'from-blue-500 to-cyan-500', x: '10%', y: '30%' },
        { step: 'Feature Engineering', icon: <Code className="w-5 h-5" />, desc: 'Extract risk indicators', color: 'from-purple-500 to-pink-500', x: '30%', y: '30%' },
        { step: 'ML Scoring', icon: <Brain className="w-5 h-5" />, desc: 'Multi-model risk analysis', color: 'from-green-500 to-emerald-500', x: '50%', y: '30%' },
        { step: 'Fraud Detection', icon: <Shield className="w-5 h-5" />, desc: 'Pattern matching & anomalies', color: 'from-red-500 to-orange-500', x: '70%', y: '30%' },
        { step: 'Policy Compliance', icon: <Lock className="w-5 h-5" />, desc: 'Regulatory checks', color: 'from-indigo-500 to-purple-500', x: '90%', y: '30%' },
        { step: 'Decision Engine', icon: <GitBranch className="w-5 h-5" />, desc: 'Approve/Review/Reject', color: 'from-pink-500 to-rose-500', x: '50%', y: '70%' },
        { step: 'Audit Trail', icon: <FileText className="w-5 h-5" />, desc: 'Complete decision log', color: 'from-teal-500 to-cyan-500', x: '70%', y: '70%' },
      ],
      stats: { applications: '2,847', approved: '1,923', flagged: '687', rejected: '237' }
    }
  };

  return (
    <div className="bg-gradient-to-br from-slate-900 via-purple-900 to-slate-800 text-white py-24">
      <div className="max-w-7xl mx-auto px-6">
        
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold mb-4">
            Build Custom AI Applications
            <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent"> for Every Department</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            From recruitment to customer support, create intelligent chatbots and applications powered by advanced workflow orchestration
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-12 overflow-x-auto">
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-2 inline-flex space-x-2 border border-white/10">
            <button
              onClick={() => { setActiveTab('recruitment'); setWorkflowStep(0); }}
              className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 whitespace-nowrap ${
                activeTab === 'recruitment'
                  ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Users className="w-4 h-4 inline mr-2 -mt-0.5" />
              Recruitment Bot
            </button>
            <button
              onClick={() => { setActiveTab('call-support'); setWorkflowStep(0); }}
              className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 whitespace-nowrap ${
                activeTab === 'call-support'
                  ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Server className="w-4 h-4 inline mr-2 -mt-0.5" />
              AI Call Support
            </button>
            <button
              onClick={() => { setActiveTab('pmo'); setWorkflowStep(0); }}
              className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 whitespace-nowrap ${
                activeTab === 'pmo'
                  ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <GitBranch className="w-4 h-4 inline mr-2 -mt-0.5" />
              PMO Platform
            </button>
            <button
              onClick={() => { setActiveTab('credit-risk'); setWorkflowStep(0); }}
              className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 whitespace-nowrap ${
                activeTab === 'credit-risk'
                  ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Shield className="w-4 h-4 inline mr-2 -mt-0.5" />
              Credit Risk App
            </button>
          </div>
        </div>

        {/* AI Application Display */}
        {Object.keys(aiApplications).includes(activeTab) && (
          <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/20">
            {/* Application Header */}
            <div className="flex justify-between items-start mb-8">
              <div className="flex items-start space-x-4">
                <div className={`w-14 h-14 bg-gradient-to-br ${aiApplications[activeTab].color} rounded-2xl flex items-center justify-center shadow-xl`}>
                  {React.cloneElement(aiApplications[activeTab].icon, { className: 'w-7 h-7' })}
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-2">{aiApplications[activeTab].name}</h3>
                  <p className="text-gray-400">{aiApplications[activeTab].description}</p>
                  <div className="flex items-center space-x-2 mt-3">
                    <Zap className="w-4 h-4 text-yellow-400" />
                    <span className="text-sm text-yellow-400 font-semibold">Powered by Advanced Workflow Orchestration</span>
                  </div>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setIsAnimating(!isAnimating)}
                  className="bg-white/10 hover:bg-white/20 p-3 rounded-lg transition-all"
                >
                  {isAnimating ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                </button>
                <button
                  onClick={() => setWorkflowStep(0)}
                  className="bg-white/10 hover:bg-white/20 p-3 rounded-lg transition-all"
                >
                  <RotateCcw className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Workflow Visualization */}
            <div className="relative h-[450px] bg-gradient-to-br from-slate-900/50 to-purple-900/50 rounded-2xl p-8 mb-6 overflow-hidden">
              {/* Connection Lines */}
              <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 0 }}>
                {aiApplications[activeTab].workflow.map((step, index) => {
                  if (index < aiApplications[activeTab].workflow.length - 1) {
                    const nextStep = aiApplications[activeTab].workflow[index + 1];
                    const isActive = index < workflowStep;
                    return (
                      <line
                        key={`line-${index}`}
                        x1={step.x}
                        y1={step.y}
                        x2={nextStep.x}
                        y2={nextStep.y}
                        stroke={isActive ? '#a78bfa' : '#374151'}
                        strokeWidth="2"
                        className="transition-all duration-500"
                      />
                    );
                  }
                  return null;
                })}
              </svg>

              {/* Workflow Nodes */}
              {aiApplications[activeTab].workflow.map((step, index) => (
                <div
                  key={`node-${index}`}
                  className="absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-500"
                  style={{ left: step.x, top: step.y }}
                >
                  <div className={`w-20 h-20 rounded-2xl flex items-center justify-center transition-all duration-500 ${
                    index <= workflowStep
                      ? `bg-gradient-to-br ${step.color} scale-100 shadow-2xl`
                      : 'bg-gray-700/80 scale-90'
                  }`}>
                    {step.icon}
                  </div>
                  <div className="absolute -bottom-20 left-1/2 transform -translate-x-1/2 text-center w-28">
                    <p className={`text-xs font-bold ${index <= workflowStep ? 'text-white' : 'text-gray-500'} mb-0.5 leading-tight`}>
                      {step.step}
                    </p>
                    <p className={`text-[10px] ${index <= workflowStep ? 'text-gray-300' : 'text-gray-600'} leading-tight`}>
                      {step.desc}
                    </p>
                  </div>
                  {index === workflowStep && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center animate-pulse shadow-xl">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
              ))}

              {/* Animated Flow Particles */}
              {isAnimating && (
                <>
                  {[...Array(3)].map((_, i) => (
                    <div
                      key={`particle-${i}`}
                      className="absolute w-2 h-2 bg-purple-400 rounded-full animate-ping"
                      style={{
                        left: aiApplications[activeTab].workflow[Math.min(workflowStep, aiApplications[activeTab].workflow.length - 1)]?.x,
                        top: aiApplications[activeTab].workflow[Math.min(workflowStep, aiApplications[activeTab].workflow.length - 1)]?.y,
                        animationDelay: `${i * 0.3}s`,
                        opacity: 0.6
                      }}
                    />
                  ))}
                </>
              )}
            </div>

            {/* Stats Dashboard */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              {Object.entries(aiApplications[activeTab].stats).map(([statKey, statValue], index) => (
                <div
                  key={statKey}
                  className={`bg-gradient-to-br ${
                    index === 0 ? 'from-blue-500/20 to-cyan-500/20 border-blue-500/30' :
                    index === 1 ? 'from-purple-500/20 to-pink-500/20 border-purple-500/30' :
                    index === 2 ? 'from-green-500/20 to-emerald-500/20 border-green-500/30' :
                    'from-orange-500/20 to-red-500/20 border-orange-500/30'
                  } border rounded-xl p-4 text-center transition-all hover:scale-105`}
                >
                  {/* <p className="text-2xl font-bold mb-1">{statValue}</p> */}
                  <p className="text-xs text-gray-400 capitalize">{statKey.replace(/([A-Z])/g, ' $1').trim()}</p>
                </div>
              ))}
            </div>

            {/* Workflow Orchestration Power */}
            <div className="bg-gradient-to-r from-purple-900/40 to-blue-900/40 border border-purple-500/30 rounded-xl p-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Zap className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-lg mb-2 flex items-center">
                    <span>Powered by Enterprise Workflow Orchestration</span>
                    <ArrowRight className="w-5 h-5 ml-2 text-purple-400" />
                  </h4>
                  <div className="grid md:grid-cols-3 gap-4 mt-4">
                    <div className="flex items-start space-x-2">
                      <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Brain className="w-4 h-4 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-purple-300">Multi-Agent AI</p>
                        <p className="text-xs text-gray-400">Parallel processing with specialized agents</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Database className="w-4 h-4 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-blue-300">Vector Search</p>
                        <p className="text-xs text-gray-400">Semantic matching & knowledge retrieval</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <GitBranch className="w-4 h-4 text-green-400" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-green-300">Smart Routing</p>
                        <p className="text-xs text-gray-400">Conditional logic & dynamic workflows</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <div className="mb-6">
            <p className="text-gray-400 text-lg mb-2">Ready to build your own AI application?</p>
            <p className="text-sm text-gray-500">Choose from our pre-built templates or create a completely custom solution</p>
          </div>
          <button className="bg-gradient-to-r from-purple-600 to-blue-600 px-8 py-4 rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all duration-300 font-semibold text-lg inline-flex items-center space-x-2 hover:shadow-2xl hover:scale-105">
            <span>Start Building Your AI Application</span>
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default HomeAgentSimulation;

// import React, { useState, useEffect } from 'react';
// import { Brain, Shield, Database, Zap, GitBranch, Code, Play, Pause, RotateCcw, CheckCircle, ArrowRight, Lock, Cloud, Server, AlertTriangle, Users, FileText, TrendingUp } from 'lucide-react';

// const HomeAgentSimulation = () => {
//   const [activeTab, setActiveTab] = useState('fraud-detection');
//   const [isAnimating, setIsAnimating] = useState(true);
//   const [fraudStep, setFraudStep] = useState(0);
//   const [complianceStep, setComplianceStep] = useState(0);

//   useEffect(() => {
//     if (!isAnimating) return;
    
//     const interval = setInterval(() => {
//       if (activeTab === 'fraud-detection') {
//         setFraudStep(prev => (prev + 1) % 8);
//       } else if (activeTab === 'compliance') {
//         setComplianceStep(prev => (prev + 1) % 7);
//       }
//     }, 2000);
    
//     return () => clearInterval(interval);
//   }, [isAnimating, activeTab]);

//   const fraudDetectionFlow = [
//     {
//       id: 'ingest',
//       title: 'Data Ingestion',
//       icon: <Database className="w-5 h-5" />,
//       description: 'Real-time transaction stream',
//       details: 'Live monitoring of transaction data',
//       color: 'from-blue-500 to-cyan-500',
//       position: { x: '8%', y: '20%' }
//     },
//     {
//       id: 'parse',
//       title: 'Feature Extraction',
//       icon: <Code className="w-5 h-5" />,
//       description: 'Extract behavioral patterns',
//       details: 'Amount, velocity, location, device fingerprint',
//       color: 'from-purple-500 to-pink-500',
//       position: { x: '25%', y: '20%' }
//     },
//     {
//       id: 'vector',
//       title: 'Vector Store Query',
//       icon: <Database className="w-5 h-5" />,
//       description: 'Semantic similarity search',
//       details: 'Compare against known fraud patterns',
//       color: 'from-green-500 to-emerald-500',
//       position: { x: '42%', y: '20%' }
//     },
//     {
//       id: 'llm',
//       title: 'LLM Analysis',
//       icon: <Brain className="w-5 h-5" />,
//       description: 'AI reasoning & context',
//       details: 'Multi-model fraud assessment',
//       color: 'from-orange-500 to-red-500',
//       position: { x: '59%', y: '20%' }
//     },
//     {
//       id: 'risk',
//       title: 'Risk Scoring',
//       icon: <TrendingUp className="w-5 h-5" />,
//       description: 'Calculate probability',
//       details: 'ML-based risk calculation',
//       color: 'from-indigo-500 to-purple-500',
//       position: { x: '76%', y: '20%' }
//     },
//     {
//       id: 'decision',
//       title: 'Decision Engine',
//       icon: <GitBranch className="w-5 h-5" />,
//       description: 'Conditional routing',
//       details: 'Approve/Flag/Block based on score',
//       color: 'from-pink-500 to-rose-500',
//       position: { x: '50%', y: '50%' }
//     },
//     {
//       id: 'alert',
//       title: 'Alert System',
//       icon: <AlertTriangle className="w-5 h-5" />,
//       description: 'Real-time notifications',
//       details: 'Multi-channel alerting',
//       color: 'from-yellow-500 to-orange-500',
//       position: { x: '30%', y: '80%' }
//     },
//     {
//       id: 'db',
//       title: 'Audit Trail',
//       icon: <FileText className="w-5 h-5" />,
//       description: 'Log all decisions',
//       details: 'Compliance & investigation',
//       color: 'from-teal-500 to-cyan-500',
//       position: { x: '70%', y: '80%' }
//     }
//   ];

//   const complianceWorkflow = {
//     name: 'Multi-Jurisdictional Document Processing',
//     trigger: 'New contract uploaded for review',
//     steps: [
//       { 
//         action: 'Document parsing & entity extraction', 
//         status: complianceStep >= 0 ? 'complete' : 'pending',
//         details: 'Extract parties, dates, amounts, clauses'
//       },
//       { 
//         action: 'Jurisdiction detection & routing', 
//         status: complianceStep >= 1 ? 'complete' : 'pending',
//         details: 'Identify applicable regulations (GDPR, CCPA, SOX)'
//       },
//       { 
//         action: 'Multi-agent compliance check', 
//         status: complianceStep >= 2 ? 'complete' : 'pending',
//         details: 'Parallel agents for data privacy, financial, legal compliance'
//       },
//       { 
//         action: 'Risk assessment & flagging', 
//         status: complianceStep >= 3 ? 'complete' : 'pending',
//         details: 'Identify non-compliant clauses & severity scoring'
//       },
//       { 
//         action: 'Cross-reference with precedents', 
//         status: complianceStep >= 4 ? 'complete' : 'pending',
//         details: 'Vector search in historical approved contracts'
//       },
//       { 
//         action: 'Generate compliance report', 
//         status: complianceStep >= 5 ? 'complete' : 'pending',
//         details: 'Detailed findings with regulation references'
//       },
//       { 
//         action: 'Route to appropriate department', 
//         status: complianceStep >= 6 ? 'complete' : 'pending',
//         details: 'Legal/Finance/Risk based on findings'
//       }
//     ]
//   };

//   const knowledgeSearchFlow = {
//     name: 'Enterprise Knowledge Search with Security Layers',
//     description: 'Multi-tenant secure search across siloed data sources',
//     layers: [
//       {
//         layer: 'Authentication',
//         icon: <Lock className="w-5 h-5" />,
//         process: 'SSO/JWT validation → Role extraction',
//         color: 'from-blue-500 to-cyan-500'
//       },
//       {
//         layer: 'Authorization',
//         icon: <Shield className="w-5 h-5" />,
//         process: 'RBAC check → Department-level access',
//         color: 'from-purple-500 to-pink-500'
//       },
//       {
//         layer: 'Query Processing',
//         icon: <Brain className="w-5 h-5" />,
//         process: 'Intent detection → Query expansion → Vector embedding',
//         color: 'from-green-500 to-emerald-500'
//       },
//       {
//         layer: 'Multi-Source Search',
//         icon: <Database className="w-5 h-5" />,
//         process: 'Parallel search: Confluence, SharePoint, Drive, Slack',
//         color: 'from-orange-500 to-red-500'
//       },
//       {
//         layer: 'Security Filtering',
//         icon: <Shield className="w-5 h-5" />,
//         process: 'Post-retrieval access control → PII masking',
//         color: 'from-indigo-500 to-purple-500'
//       },
//       {
//         layer: 'Result Ranking',
//         icon: <TrendingUp className="w-5 h-5" />,
//         process: 'Relevance scoring → Personalization → Audit logging',
//         color: 'from-pink-500 to-rose-500'
//       }
//     ]
//   };

//   const customerJourneyOrchestration = {
//     name: 'Intelligent Customer Journey Orchestration',
//     scenario: 'High-value B2B lead nurturing',
//     workflow: [
//       {
//         stage: 'Lead Capture',
//         agents: ['Form Agent', 'Enrichment Agent'],
//         actions: ['Parse form data', 'Enrich with Clearbit/LinkedIn', 'Score lead quality']
//       },
//       {
//         stage: 'Intent Analysis',
//         agents: ['Behavioral Agent', 'Content Agent'],
//         actions: ['Track website behavior', 'Analyze content consumption', 'Predict buying intent']
//       },
//       {
//         stage: 'Dynamic Routing',
//         agents: ['Routing Agent', 'Personalization Agent'],
//         actions: ['Route to SDR/AE based on score', 'Generate personalized outreach', 'Schedule optimal contact time']
//       },
//       {
//         stage: 'Multi-Touch Engagement',
//         agents: ['Email Agent', 'LinkedIn Agent', 'Retargeting Agent'],
//         actions: ['Coordinated multi-channel outreach', 'A/B test messaging', 'Track engagement']
//       },
//       {
//         stage: 'Sales Enablement',
//         agents: ['Research Agent', 'Briefing Agent'],
//         actions: ['Company research', 'Stakeholder mapping', 'Generate call brief']
//       }
//     ]
//   };

//   return (
//     <div className="bg-gradient-to-br from-slate-900 via-purple-900 to-slate-800 text-white py-24">
//       <div className="max-w-7xl mx-auto px-6">
        
//         {/* Section Header */}
//         <div className="text-center mb-16">
//           <h2 className="text-4xl lg:text-5xl font-bold mb-4">
//             Enterprise-Grade
//             <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent"> Complex Workflows</span>
//           </h2>
//           <p className="text-xl text-gray-400 max-w-3xl mx-auto">
//             Real-world enterprise scenarios with multi-agent orchestration, security layers, and intelligent decision-making
//           </p>
//         </div>

//         {/* Tab Navigation */}
//         <div className="flex justify-center mb-12 overflow-x-auto">
//           <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-2 inline-flex space-x-2 border border-white/10">
//             <button
//               onClick={() => { setActiveTab('fraud-detection'); setFraudStep(0); }}
//               className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 whitespace-nowrap ${
//                 activeTab === 'fraud-detection'
//                   ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
//                   : 'text-gray-400 hover:text-white'
//               }`}
//             >
//               <Shield className="w-4 h-4 inline mr-2" />
//               Fraud Detection
//             </button>
//             <button
//               onClick={() => { setActiveTab('compliance'); setFraudStep(0); }}
//               className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 whitespace-nowrap ${
//                 activeTab === 'compliance'
//                   ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
//                   : 'text-gray-400 hover:text-white'
//               }`}
//             >
//               <FileText className="w-4 h-4 inline mr-2" />
//               Compliance
//             </button>
//             <button
//               onClick={() => { setActiveTab('knowledge'); setFraudStep(0); }}
//               className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 whitespace-nowrap ${
//                 activeTab === 'knowledge'
//                   ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
//                   : 'text-gray-400 hover:text-white'
//               }`}
//             >
//               <Database className="w-4 h-4 inline mr-2" />
//               Knowledge Search
//             </button>
//             <button
//               onClick={() => { setActiveTab('journey'); setFraudStep(0); }}
//               className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 whitespace-nowrap ${
//                 activeTab === 'journey'
//                   ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
//                   : 'text-gray-400 hover:text-white'
//               }`}
//             >
//               <Users className="w-4 h-4 inline mr-2" />
//               Customer Journey
//             </button>
//           </div>
//         </div>

//         {/* Fraud Detection Tab */}
//         {activeTab === 'fraud-detection' && (
//           <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/20">
//             <div className="flex justify-between items-center mb-8">
//               <div>
//                 <h3 className="text-2xl font-bold mb-2">Real-Time Fraud Detection Pipeline</h3>
//                 <p className="text-gray-400">Multi-stage analysis with ML, vector search, and LLM reasoning</p>
//               </div>
//               <div className="flex space-x-2">
//                 <button
//                   onClick={() => setIsAnimating(!isAnimating)}
//                   className="bg-white/10 hover:bg-white/20 p-3 rounded-lg transition-all"
//                 >
//                   {isAnimating ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
//                 </button>
//                 <button
//                   onClick={() => setFraudStep(0)}
//                   className="bg-white/10 hover:bg-white/20 p-3 rounded-lg transition-all"
//                 >
//                   <RotateCcw className="w-5 h-5" />
//                 </button>
//               </div>
//             </div>

//             {/* Complex Flow Visualization */}
//             <div className="relative h-96 bg-gradient-to-br from-slate-900/50 to-purple-900/50 rounded-2xl p-8 mb-6">
//               {/* Connection Lines */}
//               <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 0 }}>
//                 {/* Top row connections */}
//                 {fraudDetectionFlow.slice(0, 5).map((step, index) => {
//                   if (index < 4) {
//                     const nextStep = fraudDetectionFlow[index + 1];
//                     const isActive = index <= fraudStep;
//                     return (
//                       <line
//                         key={`top-${index}`}
//                         x1={step.position.x}
//                         y1={step.position.y}
//                         x2={nextStep.position.x}
//                         y2={nextStep.position.y}
//                         stroke={isActive ? '#a78bfa' : '#374151'}
//                         strokeWidth="2"
//                         className="transition-all duration-500"
//                       />
//                     );
//                   }
//                   return null;
//                 })}
//                 {/* Connections to decision */}
//                 <line x1="76%" y1="20%" x2="50%" y2="50%" stroke={fraudStep >= 5 ? '#a78bfa' : '#374151'} strokeWidth="2" />
//                 {/* Connections from decision */}
//                 <line x1="50%" y1="50%" x2="30%" y2="80%" stroke={fraudStep >= 6 ? '#a78bfa' : '#374151'} strokeWidth="2" />
//                 <line x1="50%" y1="50%" x2="70%" y2="80%" stroke={fraudStep >= 7 ? '#a78bfa' : '#374151'} strokeWidth="2" />
//               </svg>

//               {/* Nodes */}
//               {fraudDetectionFlow.map((step, index) => (
//                 <div
//                   key={step.id}
//                   className="absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-500"
//                   style={{ left: step.position.x, top: step.position.y }}
//                 >
//                   <div className={`w-20 h-20 rounded-xl flex items-center justify-center transition-all duration-500 ${
//                     index <= fraudStep
//                       ? `bg-gradient-to-br ${step.color} scale-110 shadow-2xl`
//                       : 'bg-gray-700 scale-100'
//                   }`}>
//                     {step.icon}
//                   </div>
//                   <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 text-center w-24">
//                     <p className={`text-xs font-semibold ${index <= fraudStep ? 'text-white' : 'text-gray-500'}`}>
//                       {step.title}
//                     </p>
//                   </div>
//                   {index === fraudStep && (
//                     <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-400 rounded-full flex items-center justify-center animate-pulse">
//                       <div className="w-3 h-3 bg-white rounded-full"></div>
//                     </div>
//                   )}
//                 </div>
//               ))}
//             </div>

//             {/* Current Step Details */}
//             <div className="bg-white/5 rounded-xl p-6 border border-white/10">
//               <div className="grid md:grid-cols-2 gap-6">
//                 <div>
//                   <div className="flex items-center space-x-3 mb-4">
//                     <div className={`w-12 h-12 bg-gradient-to-br ${fraudDetectionFlow[fraudStep].color} rounded-lg flex items-center justify-center`}>
//                       {fraudDetectionFlow[fraudStep].icon}
//                     </div>
//                     <div>
//                       <p className="text-sm text-purple-400 font-semibold">Current Stage</p>
//                       <p className="text-lg font-bold">{fraudDetectionFlow[fraudStep].title}</p>
//                     </div>
//                   </div>
//                   <p className="text-sm text-gray-400 mb-2">{fraudDetectionFlow[fraudStep].description}</p>
//                   <p className="text-xs text-gray-500">{fraudDetectionFlow[fraudStep].details}</p>
//                 </div>
//                 <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-lg p-4">
//                   <p className="text-sm font-semibold mb-3 text-red-400">Live Transaction Analysis</p>
//                   <div className="space-y-2 text-sm">
//                     <div className="flex justify-between">
//                       <span className="text-gray-400">Transaction ID:</span>
//                       <span className="font-mono text-white">TXN-8473829</span>
//                     </div>
//                     <div className="flex justify-between">
//                       <span className="text-gray-400">Amount:</span>
//                       <span className="font-mono text-white">$12,450.00</span>
//                     </div>
//                     <div className="flex justify-between">
//                       <span className="text-gray-400">Risk Score:</span>
//                       <span className={`font-mono ${fraudStep >= 4 ? 'text-red-400' : 'text-gray-500'}`}>
//                         {fraudStep >= 4 ? '87.3%' : 'Calculating...'}
//                       </span>
//                     </div>
//                     <div className="flex justify-between">
//                       <span className="text-gray-400">Decision:</span>
//                       <span className={`font-semibold ${fraudStep >= 5 ? 'text-yellow-400' : 'text-gray-500'}`}>
//                         {fraudStep >= 5 ? 'FLAGGED' : 'Pending...'}
//                       </span>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         )}

//         {/* Compliance Tab */}
//         {activeTab === 'compliance' && (
//           <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/20">
//             <div className="flex justify-between items-center mb-8">
//               <div>
//                 <h3 className="text-2xl font-bold mb-2">{complianceWorkflow.name}</h3>
//                 <p className="text-gray-400">Multi-agent parallel processing with jurisdiction-aware compliance checks</p>
//               </div>
//               <div className="flex space-x-2">
//                 <button
//                   onClick={() => setIsAnimating(!isAnimating)}
//                   className="bg-white/10 hover:bg-white/20 p-3 rounded-lg transition-all"
//                 >
//                   {isAnimating ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
//                 </button>
//                 <button
//                   onClick={() => setComplianceStep(0)}
//                   className="bg-white/10 hover:bg-white/20 p-3 rounded-lg transition-all"
//                 >
//                   <RotateCcw className="w-5 h-5" />
//                 </button>
//               </div>
//             </div>

//             {/* Parallel Agent Visualization */}
//             <div className="relative h-80 bg-gradient-to-br from-slate-900/50 to-purple-900/50 rounded-2xl p-8 mb-6">
//               <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 0 }}>
//                 {/* Document to Agents */}
//                 <line x1="15%" y1="50%" x2="42%" y2="25%" stroke={complianceStep >= 2 ? '#a78bfa' : '#374151'} strokeWidth="2" />
//                 <line x1="15%" y1="50%" x2="50%" y2="50%" stroke={complianceStep >= 2 ? '#a78bfa' : '#374151'} strokeWidth="2" />
//                 <line x1="15%" y1="50%" x2="42%" y2="75%" stroke={complianceStep >= 2 ? '#a78bfa' : '#374151'} strokeWidth="2" />
//                 {/* Agents to Report */}
//                 <line x1="58%" y1="25%" x2="85%" y2="50%" stroke={complianceStep >= 4 ? '#a78bfa' : '#374151'} strokeWidth="2" />
//                 <line x1="58%" y1="50%" x2="85%" y2="50%" stroke={complianceStep >= 4 ? '#a78bfa' : '#374151'} strokeWidth="2" />
//                 <line x1="58%" y1="75%" x2="85%" y2="50%" stroke={complianceStep >= 4 ? '#a78bfa' : '#374151'} strokeWidth="2" />
//               </svg>

//               {/* Document Input */}
//               <div className="absolute" style={{ left: '15%', top: '50%', transform: 'translate(-50%, -50%)' }}>
//                 <div className={`w-20 h-20 rounded-xl flex flex-col items-center justify-center transition-all duration-500 ${
//                   complianceStep >= 0 ? 'bg-gradient-to-br from-blue-500 to-cyan-500 shadow-2xl' : 'bg-gray-700'
//                 }`}>
//                   <FileText className="w-5 h-5 mb-1" />
//                   <span className="text-xs font-semibold">Document</span>
//                 </div>
//               </div>

//               {/* Parallel Agents */}
//               <div className="absolute" style={{ left: '50%', top: '25%', transform: 'translate(-50%, -50%)' }}>
//                 <div className={`w-24 h-16 rounded-xl flex flex-col items-center justify-center transition-all duration-500 ${
//                   complianceStep >= 2 ? 'bg-gradient-to-br from-purple-500 to-pink-500 shadow-2xl' : 'bg-gray-700'
//                 }`}>
//                   <Shield className="w-4 h-4 mb-1" />
//                   <span className="text-xs font-semibold">GDPR</span>
//                 </div>
//               </div>
//               <div className="absolute" style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}>
//                 <div className={`w-24 h-16 rounded-xl flex flex-col items-center justify-center transition-all duration-500 ${
//                   complianceStep >= 2 ? 'bg-gradient-to-br from-green-500 to-emerald-500 shadow-2xl' : 'bg-gray-700'
//                 }`}>
//                   <Lock className="w-4 h-4 mb-1" />
//                   <span className="text-xs font-semibold">CCPA</span>
//                 </div>
//               </div>
//               <div className="absolute" style={{ left: '50%', top: '75%', transform: 'translate(-50%, -50%)' }}>
//                 <div className={`w-24 h-16 rounded-xl flex flex-col items-center justify-center transition-all duration-500 ${
//                   complianceStep >= 2 ? 'bg-gradient-to-br from-orange-500 to-red-500 shadow-2xl' : 'bg-gray-700'
//                 }`}>
//                   <TrendingUp className="w-4 h-4 mb-1" />
//                   <span className="text-xs font-semibold">SOX</span>
//                 </div>
//               </div>

//               {/* Report Output */}
//               <div className="absolute" style={{ left: '85%', top: '50%', transform: 'translate(-50%, -50%)' }}>
//                 <div className={`w-20 h-20 rounded-xl flex flex-col items-center justify-center transition-all duration-500 ${
//                   complianceStep >= 5 ? 'bg-gradient-to-br from-indigo-500 to-purple-500 shadow-2xl' : 'bg-gray-700'
//                 }`}>
//                   <CheckCircle className="w-5 h-5 mb-1" />
//                   <span className="text-xs font-semibold">Report</span>
//                 </div>
//               </div>

//               {/* Active Indicator */}
//               {isAnimating && complianceStep === 2 && (
//                 <>
//                   <div className="absolute w-4 h-4 bg-purple-400 rounded-full animate-ping" style={{ left: '50%', top: '25%', transform: 'translate(-50%, -50%)' }} />
//                   <div className="absolute w-4 h-4 bg-green-400 rounded-full animate-ping" style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }} />
//                   <div className="absolute w-4 h-4 bg-orange-400 rounded-full animate-ping" style={{ left: '50%', top: '75%', transform: 'translate(-50%, -50%)' }} />
//                 </>
//               )}
//             </div>

//             {/* Progress Indicators */}
//             <div className="grid grid-cols-7 gap-2 mb-6">
//               {complianceWorkflow.steps.map((step, index) => (
//                 <div
//                   key={index}
//                   className={`text-center p-3 rounded-lg transition-all ${
//                     step.status === 'complete' ? 'bg-green-500/20 border border-green-500/30' : 'bg-white/5 border border-white/10'
//                   }`}
//                 >
//                   {step.status === 'complete' ? (
//                     <CheckCircle className="w-5 h-5 mx-auto text-green-400" />
//                   ) : (
//                     <div className="w-5 h-5 mx-auto rounded-full bg-gray-600 flex items-center justify-center">
//                       <span className="text-xs font-bold">{index + 1}</span>
//                     </div>
//                   )}
//                   <p className="text-xs mt-2 text-gray-400">{step.action.split(' ')[0]}</p>
//                 </div>
//               ))}
//             </div>

//             {/* Current Status */}
//             <div className="bg-white/5 rounded-xl p-6 border border-white/10">
//               <div className="flex items-center justify-between">
//                 <div>
//                   <p className="text-sm text-purple-400 font-semibold mb-1">Current Stage</p>
//                   <p className="text-lg font-bold">{complianceWorkflow.steps[complianceStep]?.action}</p>
//                   <p className="text-sm text-gray-400 mt-1">{complianceWorkflow.steps[complianceStep]?.details}</p>
//                 </div>
//                 {complianceStep >= 2 && complianceStep < 4 && (
//                   <div className="text-right">
//                     <p className="text-sm text-gray-400 mb-2">Parallel Processing</p>
//                     <div className="flex space-x-2">
//                       <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce"></div>
//                       <div className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
//                       <div className="w-2 h-2 bg-orange-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
//                     </div>
//                   </div>
//                 )}
//               </div>
//             </div>
//           </div>
//         )}

//         {/* Knowledge Search Tab */}
//         {activeTab === 'knowledge' && (
//           <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/20">
//             <div className="flex justify-between items-center mb-8">
//               <div>
//                 <h3 className="text-2xl font-bold mb-2">{knowledgeSearchFlow.name}</h3>
//                 <p className="text-gray-400">{knowledgeSearchFlow.description}</p>
//               </div>
//               <div className="flex space-x-2">
//                 <button
//                   onClick={() => setIsAnimating(!isAnimating)}
//                   className="bg-white/10 hover:bg-white/20 p-3 rounded-lg transition-all"
//                 >
//                   {isAnimating ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
//                 </button>
//               </div>
//             </div>

//             {/* Data Flow Visualization */}
//             <div className="relative h-80 bg-gradient-to-br from-slate-900/50 to-purple-900/50 rounded-2xl p-8 mb-6">
//               {/* Connection Lines */}
//               <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 0 }}>
//                 <line x1="10%" y1="50%" x2="20%" y2="50%" stroke="#a78bfa" strokeWidth="2" />
//                 <line x1="24%" y1="50%" x2="32%" y2="50%" stroke="#a78bfa" strokeWidth="2" />
//                 <line x1="36%" y1="50%" x2="44%" y2="50%" stroke="#a78bfa" strokeWidth="2" />
//                 <line x1="48%" y1="50%" x2="56%" y2="50%" stroke="#a78bfa" strokeWidth="2" />
//                 <line x1="60%" y1="50%" x2="68%" y2="50%" stroke="#a78bfa" strokeWidth="2" />
//                 <line x1="72%" y1="50%" x2="80%" y2="50%" stroke="#a78bfa" strokeWidth="2" />
//                 <line x1="84%" y1="50%" x2="90%" y2="50%" stroke="#a78bfa" strokeWidth="2" />
//               </svg>

//               {/* Query Input */}
//               <div className="absolute" style={{ left: '10%', top: '50%', transform: 'translate(-50%, -50%)' }}>
//                 <div className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl p-3 shadow-2xl w-16 h-16 flex flex-col items-center justify-center">
//                   <Users className="w-4 h-4 mb-1" />
//                   <p className="text-xs font-semibold">Query</p>
//                 </div>
//               </div>

//               {/* Security Layers */}
//               {[0, 1, 2, 3, 4, 5].map((index) => {
//                 const layer = knowledgeSearchFlow.layers[index];
//                 const xPos = 22 + (index * 12);
//                 const isActive = isAnimating && (Math.floor(Date.now() / 2000) % 6) === index;
                
//                 return (
//                   <div
//                     key={index}
//                     className="absolute transition-all duration-500"
//                     style={{ left: `${xPos}%`, top: '50%', transform: 'translate(-50%, -50%)' }}
//                   >
//                     <div className={`w-16 h-16 rounded-xl flex flex-col items-center justify-center transition-all ${
//                       isActive ? `bg-gradient-to-br ${layer.color} shadow-2xl` : 'bg-gray-700'
//                     }`}>
//                       <div className="scale-75">{layer.icon}</div>
//                       <p className="text-xs mt-1 text-center leading-tight">{layer.layer.split(' ')[0]}</p>
//                     </div>
//                     {isActive && (
//                       <div className="absolute -top-2 -right-2 w-4 h-4 bg-green-400 rounded-full animate-pulse" />
//                     )}
//                   </div>
//                 );
//               })}

//               {/* Result Output */}
//               <div className="absolute" style={{ left: '90%', top: '50%', transform: 'translate(-50%, -50%)' }}>
//                 <div className="bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl p-3 shadow-2xl w-16 h-16 flex flex-col items-center justify-center">
//                   <CheckCircle className="w-4 h-4 mb-1" />
//                   <p className="text-xs font-semibold">Result</p>
//                 </div>
//               </div>

//               {/* Active Flow Indicator */}
//               {isAnimating && (
//                 <div 
//                   className="absolute w-3 h-3 bg-purple-400 rounded-full animate-ping"
//                   style={{ 
//                     left: `${22 + ((Math.floor(Date.now() / 2000) % 6) * 12)}%`,
//                     top: '50%',
//                     transform: 'translate(-50%, -50%)'
//                   }}
//                 />
//               )}
//             </div>

//             {/* Layer Details */}
//             <div className="grid md:grid-cols-2 gap-4">
//               {knowledgeSearchFlow.layers.map((layer, index) => (
//                 <div
//                   key={index}
//                   className={`bg-white/5 rounded-xl p-4 border transition-all ${
//                     isAnimating && (Math.floor(Date.now() / 2000) % 6) === index
//                       ? 'border-purple-400 bg-white/10'
//                       : 'border-white/10'
//                   }`}
//                 >
//                   <div className="flex items-center space-x-3 mb-2">
//                     <div className={`w-10 h-10 bg-gradient-to-br ${layer.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
//                       {layer.icon}
//                     </div>
//                     <div>
//                       <h4 className="font-bold text-sm">{layer.layer}</h4>
//                       <span className="text-xs text-gray-500">Layer {index + 1}</span>
//                     </div>
//                   </div>
//                   <p className="text-xs text-gray-400 font-mono">{layer.process}</p>
//                 </div>
//               ))}
//             </div>
//           </div>
//         )}

//         {/* Customer Journey Tab */}
//         {activeTab === 'journey' && (
//           <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/20">
//             <div className="flex justify-between items-center mb-8">
//               <div>
//                 <h3 className="text-2xl font-bold mb-2">{customerJourneyOrchestration.name}</h3>
//                 <p className="text-gray-400">{customerJourneyOrchestration.scenario}</p>
//               </div>
//               <div className="flex space-x-2">
//                 <button
//                   onClick={() => setIsAnimating(!isAnimating)}
//                   className="bg-white/10 hover:bg-white/20 p-3 rounded-lg transition-all"
//                 >
//                   {isAnimating ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
//                 </button>
//               </div>
//             </div>

//             {/* Journey Funnel Visualization */}
//             <div className="relative h-80 bg-gradient-to-br from-slate-900/50 to-purple-900/50 rounded-2xl p-8 mb-6">
//               {/* Connection Lines */}
//               <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 0 }}>
//                 {[0, 1, 2, 3, 4].map((index) => {
//                   const x1 = 15 + (index * 16);
//                   const x2 = 15 + ((index + 1) * 16);
//                   const currentStage = isAnimating ? (Math.floor(Date.now() / 2000) % 5) : 0;
//                   const isActive = index < currentStage;
//                   return (
//                     <line 
//                       key={index}
//                       x1={`${x1}%`} 
//                       y1="50%" 
//                       x2={`${x2}%`} 
//                       y2="50%" 
//                       stroke={isActive ? '#a78bfa' : '#374151'}
//                       strokeWidth="2" 
//                     />
//                   );
//                 })}
//               </svg>

//               {/* Lead Input */}
//               <div className="absolute" style={{ left: '7%', top: '50%', transform: 'translate(-50%, -50%)' }}>
//                 <div className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl p-3 shadow-2xl w-16 h-16 flex flex-col items-center justify-center">
//                   <Users className="w-4 h-4 mb-1" />
//                   <p className="text-xs font-semibold">Lead</p>
//                 </div>
//               </div>

//               {/* Journey Stages */}
//               {customerJourneyOrchestration.workflow.map((stage, index) => {
//                 const xPos = 15 + (index * 16);
//                 const currentStage = isAnimating ? (Math.floor(Date.now() / 2000) % 5) : 0;
//                 const isActive = index === currentStage;
                
//                 return (
//                   <div
//                     key={index}
//                     className="absolute transition-all duration-500"
//                     style={{ left: `${xPos}%`, top: '50%', transform: 'translate(-50%, -50%)' }}
//                   >
//                     <div className={`rounded-xl p-3 border transition-all ${
//                       isActive 
//                         ? 'bg-gradient-to-br from-purple-500 to-blue-600 border-purple-400 shadow-2xl' 
//                         : 'bg-gray-700 border-white/10'
//                     }`} style={{ width: '120px' }}>
//                       <div className="flex items-center justify-between mb-2">
//                         <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
//                           isActive ? 'bg-white/20' : 'bg-white/10'
//                         }`}>
//                           <span className="font-bold text-xs">{index + 1}</span>
//                         </div>
//                         {isActive && (
//                           <div className="flex space-x-1">
//                             <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce"></div>
//                             <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
//                             <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
//                           </div>
//                         )}
//                       </div>
//                       <h4 className="font-bold text-xs mb-1 leading-tight">{stage.stage}</h4>
//                       <div className="flex flex-wrap gap-1">
//                         {stage.agents.slice(0, 1).map((agent, idx) => (
//                           <span key={idx} className="text-xs bg-white/10 px-1.5 py-0.5 rounded leading-tight">
//                             {agent.split(' ')[0]}
//                           </span>
//                         ))}
//                       </div>
//                     </div>
//                   </div>
//                 );
//               })}

//               {/* Conversion Output */}
//               <div className="absolute" style={{ left: '93%', top: '50%', transform: 'translate(-50%, -50%)' }}>
//                 <div className="bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl p-3 shadow-2xl w-16 h-16 flex flex-col items-center justify-center">
//                   <CheckCircle className="w-4 h-4 mb-1" />
//                   <p className="text-xs font-semibold">Deal</p>
//                 </div>
//               </div>
//             </div>

//             {/* Multi-Agent Coordination */}
//             <div className="bg-white/5 rounded-xl p-6 border border-white/10">
//               <h4 className="font-bold mb-4 flex items-center space-x-2">
//                 <Brain className="w-5 h-5 text-purple-400" />
//                 <span>Multi-Agent Orchestration</span>
//               </h4>
//               <div className="grid md:grid-cols-3 gap-4">
//                 <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-lg p-4">
//                   <p className="text-sm font-semibold mb-2">Behavioral Agents</p>
//                   <p className="text-xs text-gray-400">Track engagement, predict intent, score quality</p>
//                 </div>
//                 <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-lg p-4">
//                   <p className="text-sm font-semibold mb-2">Channel Agents</p>
//                   <p className="text-xs text-gray-400">Coordinate email, LinkedIn, ads across touchpoints</p>
//                 </div>
//                 <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-lg p-4">
//                   <p className="text-sm font-semibold mb-2">Intelligence Agents</p>
//                   <p className="text-xs text-gray-400">Research, enrich, brief sales team</p>
//                 </div>
//               </div>
//             </div>
//           </div>
//         )}

//         {/* Bottom CTA */}
//         <div className="mt-16 text-center">
//           <button className="bg-gradient-to-r from-purple-600 to-blue-600 px-8 py-4 rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all duration-300 font-semibold text-lg inline-flex items-center space-x-2 hover:shadow-2xl">
//             <span>See These Scenarios in Action</span>
//             <ArrowRight className="w-5 h-5" />
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default HomeAgentSimulation; 